import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Cookie, Settings, BarChart3, Shield } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';

const CookiePolicy: React.FC = () => {
  useSEO({
    title: 'Cookiebeleid | WFT Study Buddy',
    description: 'Cookiebeleid van WFT Study Buddy. Lees welke cookies wij gebruiken en waarom.',
    canonicalUrl: '/cookie-policy',
  });
  const openCookieSettings = () => {
    // Cookiebot's built-in method to reopen the consent dialog
    if (typeof window !== 'undefined' && (window as any).Cookiebot) {
      (window as any).Cookiebot.renew();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Terug naar home</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="glass-card rounded-2xl p-8 md:p-12">
          {/* Title */}
          <div className="flex items-center gap-4 mb-8">
            <div className="w-14 h-14 bg-gradient-to-br from-orange-400 to-pink-500 rounded-xl flex items-center justify-center">
              <Cookie className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Cookiebeleid</h1>
              <p className="text-gray-500 mt-1" suppressHydrationWarning>Laatst bijgewerkt: {new Date().toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
            </div>
          </div>

          {/* Cookie Settings Button */}
          <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl p-6 mb-10 text-white">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h3 className="font-semibold text-lg">Cookie-instellingen aanpassen</h3>
                <p className="text-blue-100 text-sm mt-1">Wijzig uw cookievoorkeuren op elk moment</p>
              </div>
              <button
                onClick={openCookieSettings}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-blue-600 rounded-lg font-medium hover:bg-blue-50 transition-colors"
              >
                <Settings className="w-5 h-5" />
                Instellingen wijzigen
              </button>
            </div>
          </div>

          {/* Introduction */}
          <section className="mb-10">
            <p className="text-gray-700 leading-relaxed">
              WFT Study Buddy gebruikt cookies en vergelijkbare technologieën om onze website te laten 
              functioneren, uw voorkeuren te onthouden en inzicht te krijgen in hoe bezoekers onze website 
              gebruiken. In dit cookiebeleid leggen wij uit welke cookies wij gebruiken en waarvoor.
            </p>
          </section>

          {/* Section 1 */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center text-sm font-bold">1</span>
              Wat zijn cookies?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Cookies zijn kleine tekstbestanden die op uw computer of mobiele apparaat worden opgeslagen 
                wanneer u onze website bezoekt. Ze helpen ons om uw voorkeuren te onthouden, de website 
                te laten functioneren en inzicht te krijgen in hoe bezoekers onze website gebruiken.
              </p>
            </div>
          </section>

          {/* Section 2 - Cookie Categories */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center text-sm font-bold">2</span>
              Welke cookies gebruiken wij?
            </h2>
            
            {/* Necessary Cookies */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Noodzakelijke cookies</h3>
                  <p className="text-gray-600 text-sm mb-3">
                    Deze cookies zijn essentieel voor het functioneren van de website en kunnen niet worden 
                    uitgeschakeld. Ze worden alleen geplaatst als reactie op uw acties, zoals het instellen 
                    van privacyvoorkeuren of het inloggen.
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-green-200">
                          <th className="text-left py-2 font-medium text-gray-700">Cookie</th>
                          <th className="text-left py-2 font-medium text-gray-700">Doel</th>
                          <th className="text-left py-2 font-medium text-gray-700">Bewaartermijn</th>
                        </tr>
                      </thead>
                      <tbody className="text-gray-600">
                        <tr className="border-b border-green-100">
                          <td className="py-2 font-mono text-xs">CookieConsent</td>
                          <td className="py-2">Onthouden van cookie-voorkeuren</td>
                          <td className="py-2">1 jaar</td>
                        </tr>
                        <tr>
                          <td className="py-2 font-mono text-xs">firebase-auth</td>
                          <td className="py-2">Authenticatie sessie</td>
                          <td className="py-2">Sessie</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Analytics Cookies */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Analytische cookies</h3>
                  <p className="text-gray-600 text-sm mb-3">
                    Deze cookies helpen ons te begrijpen hoe bezoekers onze website gebruiken. De gegevens 
                    worden geanonimiseerd verzameld en helpen ons om de website te verbeteren. Deze cookies 
                    worden alleen geplaatst met uw toestemming.
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-blue-200">
                          <th className="text-left py-2 font-medium text-gray-700">Cookie</th>
                          <th className="text-left py-2 font-medium text-gray-700">Aanbieder</th>
                          <th className="text-left py-2 font-medium text-gray-700">Doel</th>
                          <th className="text-left py-2 font-medium text-gray-700">Bewaartermijn</th>
                        </tr>
                      </thead>
                      <tbody className="text-gray-600">
                        <tr className="border-b border-blue-100">
                          <td className="py-2 font-mono text-xs">_ga</td>
                          <td className="py-2">Google Analytics</td>
                          <td className="py-2">Unieke bezoekers onderscheiden</td>
                          <td className="py-2">2 jaar</td>
                        </tr>
                        <tr className="border-b border-blue-100">
                          <td className="py-2 font-mono text-xs">_ga_*</td>
                          <td className="py-2">Google Analytics</td>
                          <td className="py-2">Sessie status beheren</td>
                          <td className="py-2">2 jaar</td>
                        </tr>
                        <tr>
                          <td className="py-2 font-mono text-xs">_gid</td>
                          <td className="py-2">Google Analytics</td>
                          <td className="py-2">Bezoekers onderscheiden</td>
                          <td className="py-2">24 uur</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Marketing Cookies - Currently not used */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-gray-400 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Marketing cookies</h3>
                  <p className="text-gray-500 text-sm">
                    Wij gebruiken momenteel geen marketing cookies. Mocht dit in de toekomst veranderen, 
                    dan zullen wij dit cookiebeleid bijwerken en uw toestemming vragen.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center text-sm font-bold">3</span>
              Google Consent Mode v2
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Wij maken gebruik van Google Consent Mode v2. Dit betekent dat:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Google Analytics alleen volledige data verzamelt als u toestemming geeft</li>
                <li>Zonder toestemming worden alleen geanonimiseerde, geaggregeerde gegevens verzameld</li>
                <li>Uw privacy-keuzes worden gerespecteerd en doorgegeven aan Google</li>
              </ul>
            </div>
          </section>

          {/* Section 4 */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center text-sm font-bold">4</span>
              Cookies beheren
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                U kunt uw cookie-voorkeuren op elk moment wijzigen door op de knop bovenaan deze pagina 
                te klikken, of door op het Cookiebot-icoon in de hoek van de website te klikken.
              </p>
              <p>
                Daarnaast kunt u cookies beheren of verwijderen via uw browserinstellingen:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>
                  <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    Google Chrome
                  </a>
                </li>
                <li>
                  <a href="https://support.mozilla.org/nl/kb/cookies-verwijderen-gegevens-wissen-websites-opgeslagen" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    Mozilla Firefox
                  </a>
                </li>
                <li>
                  <a href="https://support.apple.com/nl-nl/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    Safari
                  </a>
                </li>
                <li>
                  <a href="https://support.microsoft.com/nl-nl/microsoft-edge/cookies-in-microsoft-edge-verwijderen-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    Microsoft Edge
                  </a>
                </li>
              </ul>
              <p className="mt-4 text-sm text-gray-500">
                Let op: het blokkeren van cookies kan de functionaliteit van onze website beperken.
              </p>
            </div>
          </section>

          {/* Section 5 */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center text-sm font-bold">5</span>
              Meer informatie
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Voor meer informatie over hoe wij omgaan met uw persoonsgegevens, verwijzen wij naar ons{' '}
                <Link to="/privacy" className="text-blue-600 hover:text-blue-800 underline">
                  Privacybeleid
                </Link>.
              </p>
              <p>
                Heeft u vragen over ons cookiebeleid? Neem dan contact met ons op via{' '}
             <Link to="/contact" className="text-blue-600 hover:text-blue-800 underline">
  ons contactformulier.
</Link>
              </p>
            </div>
          </section>

          {/* Powered by Cookiebot */}
          <div className="border-t border-gray-200 pt-6 mt-10">
            <p className="text-sm text-gray-500 text-center">
              Cookie consent beheerd door{' '}
              <a 
                href="https://www.cookiebot.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                Cookiebot
              </a>
            </p>
          </div>
        </div>

        {/* Back to top */}
        <div className="text-center mt-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 px-6 py-3 bg-white/80 backdrop-blur-sm rounded-xl text-gray-700 hover:bg-white hover:shadow-lg transition-all"
          >
            <ArrowLeft className="w-5 h-5" />
            Terug naar home
          </Link>
        </div>
      </main>
    </div>
  );
};

export default CookiePolicy;